<template>
  <section class="panel exposure-review-step exposure-review-step--workspace">
    <div class="exposure-hero">
      <div>
        <p class="eyebrow">Exposure review</p>
        <h2>Review address / entity exposures</h2>
        <p>
          Identify what this target appears associated with, then review the entities and addresses it
          interacts with. This review stays separate from staged starting transactions unless you drill
          into transfers and send selected rows through shared validation.
        </p>
      </div>
      <div class="exposure-hero__badges">
        <span class="analysis-chip">Source review</span>
        <span class="analysis-chip">No automatic staging</span>
        <span class="analysis-chip">{{ cacheLabel }}</span>
      </div>
    </div>

    <div class="exposure-workspace-grid">
      <form class="exposure-target-panel" @submit.prevent="emitSaveSource">
        <div class="section-heading">
          <p class="analysis-section-eyebrow">Target</p>
          <h3>Choose what to review</h3>
          <p>
            Start simple: choose an address or an attributed Arkham entity. Advanced filters are
            available, but the default review is usually enough for a first exposure scan.
          </p>
        </div>

        <div class="target-kind-grid" role="group" aria-label="Target type">
          <button
            type="button"
            class="target-kind-card"
            :class="{ 'target-kind-card--active': form.targetKind === 'address' }"
            @click="setTargetKind('address')"
          >
            <span>Address</span>
            <strong>One wallet or contract</strong>
          </button>
          <button
            type="button"
            class="target-kind-card"
            :class="{ 'target-kind-card--active': form.targetKind === 'entity' }"
            @click="setTargetKind('entity')"
          >
            <span>Entity</span>
            <strong>An attributed address group</strong>
          </button>
        </div>

        <template v-if="form.targetKind === 'address'">
          <label class="field">
            <span>Address</span>
            <input
              v-model="form.address"
              type="text"
              placeholder="0x..., bc1..., Solana address, Tron address..."
              autocomplete="off"
              data-1p-ignore
            />
          </label>

          <label class="field">
            <span>Optional address label suggestion</span>
            <input
              v-model="form.label"
              type="text"
              placeholder="Withdrawal Address #1"
              autocomplete="off"
              data-1p-ignore
            />
          </label>
        </template>

        <template v-else>
          <label class="field">
            <span>Entity search</span>
            <div class="inline-search">
              <input
                v-model="form.entityQuery"
                type="text"
                placeholder="Coinbase, Binance, Tether, Uniswap..."
                autocomplete="off"
                data-1p-ignore
                @keyup.enter.prevent="searchEntities"
              />
              <button
                type="button"
                class="btn btn--secondary"
                :disabled="entitySearchStatus === 'running' || !form.entityQuery.trim()"
                @click="searchEntities"
              >
                {{ entitySearchStatus === 'running' ? 'Searching…' : 'Search' }}
              </button>
            </div>
          </label>

          <div v-if="entitySearchResults.length" class="entity-result-list">
            <button
              v-for="result in entitySearchResults"
              :key="`${result.kind || 'entity'}:${result.id || result.entityId || result.address}`"
              type="button"
              class="entity-result-card"
              :class="{ 'entity-result-card--selected': isSelectedEntity(result) }"
              @click="selectEntity(result)"
            >
              <strong>{{ result.displayName || result.id || result.address }}</strong>
              <span>{{ result.entityId || result.address || result.kind }}</span>
              <small v-if="result.type || (result.tags && result.tags.length)">
                {{ [result.type, ...(result.tags || []).slice(0, 3)].filter(Boolean).join(' · ') }}
              </small>
            </button>
          </div>

          <article v-if="form.selectedEntity" class="selected-entity-card">
            <span>Selected entity</span>
            <strong>{{ form.selectedEntity.displayName || form.selectedEntity.entityId }}</strong>
            <code>{{ form.selectedEntity.entityId || form.selectedEntity.id }}</code>
          </article>
        </template>

        <details class="advanced-settings">
          <summary>
            <span>Advanced filters</span>
            <small>{{ filterSummary }}</small>
          </summary>

          <div class="exposure-filter-grid">
            <label class="field">
              <span>Chains</span>
              <input v-model="form.filters.chainsText" type="text" placeholder="ethereum, base, arbitrum_one" />
            </label>

            <label class="field">
              <span>Time window</span>
              <select v-model="form.filters.timeLast">
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last 1 year</option>
              </select>
            </label>

            <label class="field">
              <span>Token filter</span>
              <input v-model="form.filters.tokensText" type="text" placeholder="USDC, ethereum,0x..." />
            </label>

            <label class="field">
              <span>Minimum USD</span>
              <input v-model="form.filters.usdGte" type="number" min="0" step="0.01" placeholder="1000" />
            </label>

            <label class="field">
              <span>Rows per direction</span>
              <input v-model.number="form.filters.limit" type="number" min="1" max="100" />
            </label>
          </div>
        </details>

        <details class="advanced-settings advanced-settings--notes">
          <summary>
            <span>Review notes</span>
            <small>{{ form.note.trim() ? 'Notes added' : 'Optional' }}</small>
          </summary>
          <label class="field">
            <span>Notes</span>
            <textarea v-model="form.note" rows="4" placeholder="Why is this target relevant?"></textarea>
          </label>
        </details>

        <div v-if="error" class="validation-warning validation-warning--strong">
          {{ error }}
        </div>

        <div class="panel__actions panel__actions--wrap">
          <button type="button" class="btn btn--primary" :disabled="!canRunLookup || lookupStatus === 'running'" @click="runLookup()">
            {{ lookupStatus === 'running' ? 'Running lookup…' : 'Run exposure lookup' }}
          </button>
          <button type="button" class="btn btn--ghost" :disabled="!canRunLookup || lookupStatus === 'running'" @click="runLookup({ forceRefresh: true })">
            Refresh lookup
          </button>
          <button type="submit" class="btn btn--secondary">Save review source</button>
          <button type="button" class="btn btn--ghost" @click="$emit('cancel')">Cancel</button>
        </div>
      </form>

      <article class="exposure-dossier-card">
        <div class="exposure-dossier-card__top">
          <div>
            <p class="address-analysis-summary__eyebrow">Exposure review snapshot</p>
            <h3>{{ previewTitle }}</h3>
            <code>{{ previewIdentifier }}</code>
          </div>
          <span class="status-pill" :class="lookupStatusClass">{{ lookupStatusLabel }}</span>
        </div>

        <p class="address-analysis-summary__description">
          {{ form.note.trim() || snapshotIntroCopy }}
        </p>

        <div v-if="warnings.length" class="validation-summary validation-summary--compact">
          <p><strong>Review notes:</strong> {{ warnings.join(' ') }}</p>
        </div>

        <div class="snapshot-runway" :class="`snapshot-runway--${snapshotRunwayState}`">
          <div class="snapshot-runway__header">
            <div>
              <strong>{{ snapshotRunwayTitle }}</strong>
              <span>{{ snapshotRunwaySubtitle }}</span>
            </div>
            <span class="snapshot-runway__pulse" aria-hidden="true"></span>
          </div>

          <div class="snapshot-runway__steps" aria-label="Exposure lookup progress">
            <article
              v-for="step in snapshotRunwaySteps"
              :key="step.key"
              class="snapshot-step"
              :class="{
                'snapshot-step--complete': step.state === 'complete',
                'snapshot-step--active': step.state === 'active',
                'snapshot-step--pending': step.state === 'pending',
                'snapshot-step--warning': step.state === 'warning'
              }"
            >
              <span class="snapshot-step__icon" aria-hidden="true">
                <template v-if="step.state === 'complete'">✓</template>
                <template v-else-if="step.state === 'active'">…</template>
                <template v-else-if="step.state === 'warning'">!</template>
                <template v-else>•</template>
              </span>
              <span class="snapshot-step__copy">
                <strong>{{ step.label }}</strong>
                <small>{{ step.detail }}</small>
              </span>
            </article>
          </div>

          <div class="snapshot-runway__callout">
            <p>{{ snapshotCalloutCopy }}</p>
            <div v-if="result" class="snapshot-runway__actions">
              <button type="button" class="btn btn--secondary btn--small" @click="scrollToVisualWorkspace">
                Jump to exposure wheel ↓
              </button>
              <button type="button" class="btn btn--ghost btn--small" @click="scrollToCounterpartyDetail">
                Jump to counterparties ↓
              </button>
            </div>
          </div>
        </div>

        <div class="dossier-kpi-grid">
          <article v-for="stat in visibleStatCards" :key="stat.label" class="analysis-mini-card">
            <span class="analysis-mini-card__label">{{ stat.label }}</span>
            <strong>{{ stat.value }}</strong>
            <p>{{ stat.hint }}</p>
          </article>
        </div>
      </article>
    </div>

    <div v-if="lookupCueVisible && result" class="post-lookup-cue">
      <div>
        <strong>Lookup complete.</strong>
        <span>Scroll down to review the exposure wheel, grouped counterparties, and drilldown table.</span>
      </div>
      <button type="button" class="btn btn--secondary btn--small" @click="scrollToVisualWorkspace">
        Jump to exposure wheel ↓
      </button>
    </div>

    <section ref="visualWorkspace" class="exposure-visual-workspace">
      <div class="visual-workspace-header">
        <div>
          <p class="analysis-section-eyebrow">Exposure wheel</p>
          <h3>Grouped counterparty exposure</h3>
          <p>
            Counterparties are grouped by entity where possible so a regulator can quickly see broad
            exposure patterns before drilling into granular rows.
          </p>
        </div>
        <div class="review-toggle-stack">
          <div class="segmented-control segmented-control--compact" role="group" aria-label="Exposure direction">
            <button
              v-for="mode in exposureModes"
              :key="mode.value"
              type="button"
              class="segmented-control__button"
              :class="{ 'segmented-control__button--active': wheelMode === mode.value }"
              @click="wheelMode = mode.value"
            >
              {{ mode.label }}
            </button>
          </div>
          <div class="segmented-control segmented-control--compact" role="group" aria-label="Counterparty display mode">
            <button
              type="button"
              class="segmented-control__button"
              :class="{ 'segmented-control__button--active': counterpartyDisplayMode === 'grouped' }"
              @click="counterpartyDisplayMode = 'grouped'"
            >
              Grouped
            </button>
            <button
              type="button"
              class="segmented-control__button"
              :class="{ 'segmented-control__button--active': counterpartyDisplayMode === 'raw' }"
              @click="counterpartyDisplayMode = 'raw'"
            >
              Raw rows
            </button>
          </div>
        </div>
      </div>

      <div class="visual-workspace-grid">
        <article ref="donutCard" class="donut-card">
          <div class="donut-card__header">
            <div>
              <h3>{{ activeWheelTitle }}</h3>
              <p>{{ activeWheelSubtitle }}</p>
            </div>
            <span class="analysis-wheel-card__state">{{ activeCounterpartyGroups.length }} groups</span>
          </div>

          <div ref="wheelJumpTarget" class="donut-layout" :class="{ 'donut-layout--active': activeSegmentId }">
            <div class="donut-chart" aria-label="Grouped counterparty exposure wheel">
              <svg class="donut-chart__svg" viewBox="0 0 100 100" role="img">
                <title>{{ activeWheelTitle }}</title>
                <circle class="donut-chart__track" cx="50" cy="50" r="38" />
                <circle
                  v-for="segment in wheelSvgSegments"
                  :key="segment.id"
                  class="donut-chart__segment"
                  :class="{ 'donut-chart__segment--active': isSegmentActive(segment), 'donut-chart__segment--muted': activeSegmentId && !isSegmentActive(segment) }"
                  cx="50"
                  cy="50"
                  r="38"
                  :stroke="segment.color"
                  :stroke-dasharray="`${segment.dashLength} ${segment.gapLength}`"
                  :stroke-dashoffset="segment.dashOffset"
                  :tabindex="segment.isOther ? -1 : 0"
                  role="button"
                  @mouseenter="setActiveSegment(segment.id)"
                  @mouseleave="clearActiveSegment"
                  @focus="setActiveSegment(segment.id)"
                  @blur="clearActiveSegment"
                  @click="viewTransfersForDisplayRow(segment)"
                  @keydown.enter.prevent="viewTransfersForDisplayRow(segment)"
                  @keydown.space.prevent="viewTransfersForDisplayRow(segment)"
                />
              </svg>
              <div class="donut-chart__center">
                <span>{{ activeWheelSegment ? activeWheelSegment.displayName : 'Total exposure' }}</span>
                <strong>{{ formatUsd(activeWheelSegment ? activeWheelSegment.usd : activeExposureTotal) }}</strong>
                <small v-if="activeWheelSegment">{{ formatPercent(activeWheelSegment.usd, activeExposureTotal) }}</small>
              </div>
            </div>

            <div v-if="activeWheelSegments.length" class="donut-legend">
              <div
                v-for="segment in activeWheelSegments"
                :key="segment.id"
                class="donut-legend-row"
                :class="{ 'donut-legend-row--active': isSegmentActive(segment), 'donut-legend-row--muted': activeSegmentId && !isSegmentActive(segment), 'donut-legend-row--other': segment.isOther }"
                @mouseenter="setActiveSegment(segment.id)"
                @mouseleave="clearActiveSegment"
              >
                <span class="donut-legend-row__swatch" :style="{ background: segment.color }"></span>
                <span class="donut-legend-row__main">
                  <strong>{{ segment.displayName }}</strong>
                  <small>{{ formatUsd(segment.usd) }} · {{ formatPercent(segment.usd, activeExposureTotal) }} · {{ formatCount(segment.transactionCount) }} tx</small>
                </span>
                <button
                  type="button"
                  class="btn btn--ghost btn--small donut-legend-row__action"
                  :disabled="segment.isOther"
                  @click="viewTransfersForDisplayRow(segment)"
                >
                  View
                </button>
              </div>
            </div>
            <p v-else class="empty-state-copy">{{ activeWheelEmptyCopy }}</p>
          </div>
        </article>

        <article class="top-group-card">
          <div class="analysis-stats-card__header">
            <div>
              <p class="analysis-section-eyebrow">Top grouped counterparties</p>
              <h3>{{ activeCounterpartyGroups.length ? `${activeCounterpartyGroups.length} grouped counterparties` : 'No grouped counterparties yet' }}</h3>
              <p>
                Use this list with the wheel. The detailed table below preserves the raw provider-level rows.
              </p>
            </div>
          </div>

          <div v-if="activeCounterpartyGroups.length" class="group-list">
            <article
              v-for="group in activeCounterpartyGroups.slice(0, 12)"
              :key="group.id"
              class="group-list-row"
              :class="{ 'group-list-row--active': isSegmentActive(group), 'group-list-row--muted': activeSegmentId && !isSegmentActive(group) }"
              @mouseenter="setActiveSegment(group.id)"
              @mouseleave="clearActiveSegment"
            >
              <span class="group-list-row__main">
                <strong>{{ group.displayName }}</strong>
                <small>{{ counterpartySecondaryLine(group) || rowDirectionLabel(group) }}</small>
              </span>
              <span class="group-list-row__meta">
                <strong>{{ formatUsd(group.usd) }}</strong>
                <small>{{ formatPercent(group.usd, activeExposureTotal) }} · {{ formatCount(group.transactionCount) }} tx</small>
              </span>
              <button type="button" class="btn btn--ghost btn--small" @click="viewTransfersForDisplayRow(group)">
                View
              </button>
            </article>
          </div>
          <p v-else class="empty-state-copy">Run a lookup to populate grouped exposures.</p>
        </article>
      </div>
    </section>

    <section ref="counterpartyDetail" class="analysis-stats-card analysis-stats-card--wide">
      <div class="analysis-stats-card__header">
        <div>
          <p class="analysis-section-eyebrow">Counterparty detail</p>
          <h3>{{ counterpartyDisplayMode === 'grouped' ? 'Grouped exposure rows' : 'Raw provider rows' }}</h3>
          <p>
            Click a row to drill into transfers. Transfer rows are not staged until selected and sent
            through shared validation.
          </p>
        </div>
      </div>

      <div v-if="displayCounterpartyRows.length" class="table-scroll">
        <table class="tx-table exposure-table">
          <thead>
            <tr>
              <th>Direction</th>
              <th>Counterparty</th>
              <th>Type / tags</th>
              <th>USD volume</th>
              <th>Tx count</th>
              <th>{{ counterpartyDisplayMode === 'grouped' ? 'Rows' : 'Source' }}</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayCounterpartyRows" :key="counterpartyTableKey(row)">
              <td>{{ rowDirectionLabel(row) }}</td>
              <td>
                <div class="counterparty-cell">
                  <strong>{{ counterpartyPrimaryName(row) }}</strong>
                  <code v-if="counterpartySecondaryLine(row)">{{ counterpartySecondaryLine(row) }}</code>
                </div>
              </td>
              <td>
                <div class="chip-list" v-if="counterpartyChips(row).length">
                  <span v-for="chip in counterpartyChips(row).slice(0, 4)" :key="`${counterpartyTableKey(row)}:${chip}`" class="type-chip">
                    {{ chip }}
                  </span>
                </div>
                <span v-else>—</span>
              </td>
              <td>{{ formatUsd(row.usd) }}</td>
              <td>{{ formatCount(row.transactionCount) }}</td>
              <td>{{ counterpartyDisplayMode === 'grouped' ? formatCount(row.rowCount || 1) : 'Provider row' }}</td>
              <td>
                <div class="table-actions">
                  <template v-if="row.direction === 'mixed' && counterpartyDisplayMode === 'grouped'">
                    <button
                      v-if="hasFlow(row, 'out')"
                      type="button"
                      class="btn btn--ghost btn--small"
                      @click="viewTransfersForDisplayRow(row, 'out')"
                    >
                      Outgoing
                    </button>
                    <button
                      v-if="hasFlow(row, 'in')"
                      type="button"
                      class="btn btn--ghost btn--small"
                      @click="viewTransfersForDisplayRow(row, 'in')"
                    >
                      Incoming
                    </button>
                  </template>
                  <button v-else type="button" class="btn btn--ghost btn--small" @click="viewTransfersForDisplayRow(row)">
                    View transfers
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty-state-copy">No counterparty rows yet.</p>
    </section>

    <article v-if="transferStatus !== 'idle' || transferRows.length" class="analysis-stats-card analysis-stats-card--wide transfer-drilldown-card">
      <div class="analysis-stats-card__header">
        <div>
          <p class="analysis-section-eyebrow">Transfer drilldown</p>
          <h3>{{ transferDrilldownTitle }}</h3>
          <p>
            Select only the transfers that should become candidate starting transactions. They will
            still go through shared validation before joining the prep-run output.
          </p>
        </div>
        <span class="status-pill" :class="transferStatusClass">{{ transferStatusLabel }}</span>
      </div>

      <div v-if="transferRows.length" class="table-scroll">
        <table class="tx-table exposure-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Transaction</th>
              <th>Time</th>
              <th>From</th>
              <th>To</th>
              <th>Token</th>
              <th>USD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transfer in transferRows" :key="transferRowKey(transfer)">
              <td>
                <input type="checkbox" :checked="isTransferSelected(transfer)" @change="toggleTransferSelection(transfer)" />
              </td>
              <td><code>{{ compactValue(transfer.txHash) || 'No hash found' }}</code></td>
              <td>{{ formatDate(transfer.time) }}</td>
              <td>{{ compactValue(transfer.fromLabel || transfer.fromAddress) || '—' }}</td>
              <td>{{ compactValue(transfer.toLabel || transfer.toAddress) || '—' }}</td>
              <td>{{ transfer.tokenSymbol || transfer.tokenName || '—' }}</td>
              <td>{{ formatUsd(transfer.usd) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty-state-copy">No transfer rows loaded for this counterparty yet.</p>

      <div class="panel__actions panel__actions--wrap">
        <button type="button" class="btn btn--primary" :disabled="!selectedTransfers.length" @click="emitStageTransfers">
          Send selected transfers to validation
        </button>
        <button type="button" class="btn btn--ghost" :disabled="!selectedCounterparty || transferStatus === 'running'" @click="refreshTransfers">
          Refresh transfers
        </button>
      </div>
    </article>
  </section>
</template>

<script>
import {
  lookupExposureReview,
  lookupExposureTransfers,
  normalizeExposureFilters,
  searchExposureTargets
} from '@/services/casePrepBuddyExposureReviewService';

const WHEEL_PALETTE = [
  '#2563eb',
  '#0ea5e9',
  '#14b8a6',
  '#22c55e',
  '#f59e0b',
  '#f97316',
  '#8b5cf6',
  '#ec4899'
];

export default {
  name: 'CasePrepExposureReviewStep',
  emits: ['save-source', 'stage-transfers', 'cancel', 'flash', 'error'],
  data() {
    return {
      form: {
        targetKind: 'address',
        address: '',
        entityQuery: '',
        selectedEntity: null,
        label: '',
        note: '',
        filters: {
          chainsText: '',
          tokensText: '',
          tagsText: '',
          timeLast: '30d',
          usdGte: '',
          limit: 25
        }
      },
      lookupStatus: 'idle',
      entitySearchStatus: 'idle',
      entitySearchResults: [],
      result: null,
      transferStatus: 'idle',
      transferRows: [],
      selectedTransferKeys: [],
      selectedCounterparty: null,
      selectedFlow: 'out',
      wheelMode: 'out',
      counterpartyDisplayMode: 'grouped',
      activeSegmentId: '',
      lookupCueVisible: false,
      error: ''
    };
  },
  computed: {
    exposureModes() {
      return [
        { value: 'out', label: 'Outgoing' },
        { value: 'in', label: 'Incoming' },
        { value: 'combined', label: 'Combined' }
      ];
    },
    normalizedFilters() {
      return normalizeExposureFilters(this.form.filters);
    },
    selectedEntityId() {
      return this.form.selectedEntity?.entityId || this.form.selectedEntity?.id || '';
    },
    canRunLookup() {
      if (this.form.targetKind === 'address') return Boolean(this.form.address.trim());
      return Boolean(this.selectedEntityId);
    },
    previewTitle() {
      const identityName = this.result?.identity?.entityName || this.result?.identity?.displayName || this.result?.target?.displayName;
      if (identityName) return identityName;
      if (this.form.targetKind === 'entity' && this.form.selectedEntity) {
        return this.form.selectedEntity.displayName || this.selectedEntityId;
      }
      if (this.form.label.trim()) return this.form.label.trim();
      return this.form.targetKind === 'entity' ? 'Entity exposure review' : 'Address exposure review';
    },
    previewIdentifier() {
      if (this.form.targetKind === 'entity') return this.selectedEntityId || 'Search for and select an entity.';
      return this.form.address.trim() || 'Enter an address to review.';
    },
    defaultDescription() {
      return 'Run a provider-backed lookup to populate identity, incoming exposure, outgoing exposure, and drilldown details.';
    },
    snapshotIntroCopy() {
      if (this.lookupStatus === 'running') {
        return 'Provider lookup is running. The progress markers below will update as identity and exposure data load.';
      }
      if (this.result?.status === 'warning') {
        return 'Provider lookup returned review notes. Check the indicators below, then continue into the exposure workspace.';
      }
      if (this.result) {
        return 'Provider-backed identity and exposure results are ready. Use the indicators and jump links below to continue.';
      }
      return this.defaultDescription;
    },
    snapshotRunwayState() {
      if (this.lookupStatus === 'running') return 'running';
      if (this.result?.status === 'warning') return 'warning';
      if (this.result) return 'complete';
      return 'idle';
    },
    snapshotRunwayTitle() {
      if (this.lookupStatus === 'running') return 'Provider lookup in progress';
      if (this.result?.status === 'warning') return 'Lookup completed with review notes';
      if (this.result) {
        return this.result.cache?.hit
          ? 'Provider-backed review loaded from cache'
          : 'Provider-backed review completed';
      }
      return 'Ready to run an exposure lookup';
    },
    snapshotRunwaySubtitle() {
      if (this.lookupStatus === 'running') return 'Fetching identity and exposure data from the provider.';
      if (this.result) return 'The main review workspace is below this summary card.';
      return 'Run a lookup to populate identity, exposure, and drilldown data.';
    },
    snapshotRunwaySteps() {
      const hasResult = Boolean(this.result);
      const hasIdentity = Boolean(this.identity?.entityId || this.identity?.entityName || this.identity?.displayName);
      const hasOutgoing = this.outgoingRows.length > 0;
      const hasIncoming = this.incomingRows.length > 0;
      const running = this.lookupStatus === 'running';
      const warning = this.result?.status === 'warning';

      const stepState = (complete) => {
        if (running) return 'active';
        if (complete) return 'complete';
        if (hasResult && warning) return 'warning';
        if (hasResult) return 'complete';
        return 'pending';
      };

      return [
        {
          key: 'identity',
          label: 'Identity detected',
          detail: hasIdentity
            ? (this.identity.entityName || this.identity.displayName || this.identity.entityId || 'Provider attribution available')
            : running ? 'Checking attribution' : hasResult ? 'No provider identity returned' : 'Awaiting lookup',
          state: stepState(hasIdentity)
        },
        {
          key: 'outgoing',
          label: 'Outgoing exposure loaded',
          detail: hasOutgoing
            ? `${this.formatUsd(this.outgoingTotal)} · ${this.groupedOutgoingRows.length || this.outgoingRows.length} group${(this.groupedOutgoingRows.length || this.outgoingRows.length) === 1 ? '' : 's'}`
            : running ? 'Loading outgoing counterparties' : hasResult ? 'No outgoing rows returned' : 'No outgoing rows yet',
          state: stepState(hasOutgoing)
        },
        {
          key: 'incoming',
          label: 'Incoming exposure loaded',
          detail: hasIncoming
            ? `${this.formatUsd(this.incomingTotal)} · ${this.groupedIncomingRows.length || this.incomingRows.length} group${(this.groupedIncomingRows.length || this.incomingRows.length) === 1 ? '' : 's'}`
            : running ? 'Loading incoming counterparties' : hasResult ? 'No incoming rows returned' : 'No incoming rows yet',
          state: stepState(hasIncoming)
        },
        {
          key: 'drilldown',
          label: 'Ready for drilldown',
          detail: hasResult ? 'Use View on a counterparty row' : 'Available after lookup',
          state: running ? 'active' : hasResult ? 'complete' : 'pending'
        }
      ];
    },
    snapshotCalloutCopy() {
      if (this.lookupStatus === 'running') {
        return 'The review is running. When it completes, the wheel and counterparty tools below will show the broader exposure picture.';
      }
      if (this.result) {
        return 'Your review data is ready. The KPI cards are only a summary; jump down to inspect the wheel, grouped counterparties, and transfer drilldown tools.';
      }
      return 'Run a provider-backed lookup to populate identity, incoming exposure, outgoing exposure, and drilldown details.';
    },
    filterSummary() {
      const pieces = [];
      if (this.form.filters.timeLast) pieces.push(this.form.filters.timeLast);
      if (this.form.filters.chainsText) pieces.push('chains filtered');
      if (this.form.filters.tokensText) pieces.push('tokens filtered');
      if (this.form.filters.usdGte) pieces.push(`min ${this.formatUsd(this.form.filters.usdGte)}`);
      return pieces.join(' · ') || 'Default 30d review';
    },
    warnings() {
      return Array.isArray(this.result?.warnings) ? this.result.warnings.filter(Boolean) : [];
    },
    lookupStatusLabel() {
      if (this.lookupStatus === 'running') return 'Lookup running';
      if (this.result?.cache?.hit) return 'Cached lookup';
      if (this.result) return this.result.status === 'warning' ? 'Lookup warning' : 'Lookup complete';
      return 'Lookup not run';
    },
    lookupStatusClass() {
      if (this.lookupStatus === 'running') return 'status-pill--unverified';
      if (this.result?.status === 'warning') return 'status-pill--needs_review';
      if (this.result) return 'status-pill--ready';
      return 'status-pill--placeholder';
    },
    cacheLabel() {
      if (!this.result) return '24h cache-ready';
      if (this.result.cache?.hit) return 'Loaded from server cache';
      return this.result.cache?.expiresAt ? 'Cached for reuse' : 'Provider lookup result';
    },
    identity() {
      return this.result?.identity || {};
    },
    outgoingRows() {
      const rows = Array.isArray(this.result?.counterparties?.outgoing) ? this.result.counterparties.outgoing : [];
      return rows.map((row) => ({ ...row, direction: 'out' }));
    },
    incomingRows() {
      const rows = Array.isArray(this.result?.counterparties?.incoming) ? this.result.counterparties.incoming : [];
      return rows.map((row) => ({ ...row, direction: 'in' }));
    },
    allCounterpartyRows() {
      return [...this.outgoingRows, ...this.incomingRows];
    },
    outgoingTotal() {
      return this.outgoingRows.reduce((total, row) => total + (Number(row.usd) || 0), 0);
    },
    incomingTotal() {
      return this.incomingRows.reduce((total, row) => total + (Number(row.usd) || 0), 0);
    },
    combinedTotal() {
      return this.outgoingTotal + this.incomingTotal;
    },
    groupedOutgoingRows() {
      return this.groupCounterpartyRows(this.outgoingRows, 'directional');
    },
    groupedIncomingRows() {
      return this.groupCounterpartyRows(this.incomingRows, 'directional');
    },
    groupedCombinedRows() {
      return this.groupCounterpartyRows(this.allCounterpartyRows, 'combined');
    },
    activeRawCounterpartyRows() {
      if (this.wheelMode === 'out') return this.outgoingRows;
      if (this.wheelMode === 'in') return this.incomingRows;
      return this.allCounterpartyRows;
    },
    activeCounterpartyGroups() {
      if (this.wheelMode === 'out') return this.groupedOutgoingRows;
      if (this.wheelMode === 'in') return this.groupedIncomingRows;
      return this.groupedCombinedRows;
    },
    activeExposureTotal() {
      if (this.wheelMode === 'out') return this.outgoingTotal;
      if (this.wheelMode === 'in') return this.incomingTotal;
      return this.combinedTotal;
    },
    activeWheelTitle() {
      if (this.wheelMode === 'out') return 'Outgoing exposure wheel';
      if (this.wheelMode === 'in') return 'Incoming exposure wheel';
      return 'Combined exposure wheel';
    },
    activeWheelSubtitle() {
      if (this.wheelMode === 'out') return 'Where the reviewed target is observed sending value.';
      if (this.wheelMode === 'in') return 'Where value is observed entering the reviewed target.';
      return 'Incoming and outgoing exposure combined by counterparty.';
    },
    activeWheelEmptyCopy() {
      if (this.lookupStatus === 'running') return 'Lookup is running. Exposure groups will appear here.';
      return 'Run a lookup to populate grouped exposure data.';
    },
    activeWheelSegments() {
      const sortedGroups = (this.activeCounterpartyGroups || [])
        .filter((group) => Number(group.usd) > 0)
        .slice()
        .sort((a, b) => (Number(b.usd) || 0) - (Number(a.usd) || 0));

      const top = sortedGroups.slice(0, 8).map((group, index) => ({
        ...group,
        color: WHEEL_PALETTE[index % WHEEL_PALETTE.length],
        isOther: false
      }));

      const other = sortedGroups.slice(8);
      const otherUsd = other.reduce((total, group) => total + (Number(group.usd) || 0), 0);
      const otherTx = other.reduce((total, group) => total + (Number(group.transactionCount) || 0), 0);

      if (otherUsd > 0) {
        top.push({
          id: 'other',
          displayName: 'Other grouped counterparties',
          usd: otherUsd,
          transactionCount: otherTx,
          rowCount: other.reduce((total, group) => total + (Number(group.rowCount) || 1), 0),
          color: '#cbd5e1',
          isOther: true
        });
      }

      return top;
    },
    activeWheelSegment() {
      if (!this.activeSegmentId) return null;
      return this.activeWheelSegments.find((segment) => segment.id === this.activeSegmentId) || null;
    },
    wheelSvgSegments() {
      const radius = 38;
      const circumference = 2 * Math.PI * radius;
      const total = Number(this.activeExposureTotal) || 0;
      let offset = 0;

      return this.activeWheelSegments.map((segment) => {
        const value = Number(segment.usd) || 0;
        const share = total > 0 ? value / total : 0;
        const dashLength = Math.max(share * circumference, share > 0 ? 1.2 : 0);
        const svgSegment = {
          ...segment,
          dashLength: dashLength.toFixed(3),
          gapLength: circumference.toFixed(3),
          dashOffset: (-offset).toFixed(3)
        };
        offset += dashLength;
        return svgSegment;
      });
    },
    wheelGradient() {
      if (!this.activeWheelSegments.length || !this.activeExposureTotal) {
        return 'conic-gradient(#e2e8f0 0deg 360deg)';
      }

      let cursor = 0;
      const stops = this.activeWheelSegments.map((segment) => {
        const value = Number(segment.usd) || 0;
        const span = Math.max(0.75, (value / this.activeExposureTotal) * 360);
        const start = cursor;
        const end = Math.min(360, cursor + span);
        cursor = end;
        return `${segment.color} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`;
      });

      if (cursor < 360) {
        stops.push(`#e2e8f0 ${cursor.toFixed(2)}deg 360deg`);
      }

      return `conic-gradient(${stops.join(', ')})`;
    },
    displayCounterpartyRows() {
      const rows = this.counterpartyDisplayMode === 'grouped'
        ? this.activeCounterpartyGroups
        : this.activeRawCounterpartyRows;

      return rows
        .slice()
        .sort((a, b) => (Number(b.usd) || 0) - (Number(a.usd) || 0))
        .slice(0, 40);
    },
    statCards() {
      return [
        {
          label: 'Target',
          value: this.form.targetKind === 'entity' ? 'Entity' : 'Address',
          hint: this.form.targetKind === 'entity' ? 'Reviews an attributed address group.' : 'Reviews one wallet or contract.',
          show: true
        },
        {
          label: 'Entity / label',
          value: this.identity.entityName || this.identity.displayName || 'Pending lookup',
          hint: 'Provider attribution should be corroborated before evidentiary use.',
          show: true
        },
        {
          label: 'Address count',
          value: this.formatCount(this.identity.addressCount),
          hint: 'Most relevant for entity-level review.',
          show: Number(this.identity.addressCount || 0) > 0
        },
        {
          label: 'Total balance',
          value: this.formatUsd(this.identity.totalBalanceUsd),
          hint: 'Summary value when available from provider.',
          show: Number(this.identity.totalBalanceUsd || 0) > 0
        },
        {
          label: 'Outgoing exposure',
          value: this.formatUsd(this.outgoingTotal),
          hint: `${this.groupedOutgoingRows.length || this.outgoingRows.length} outgoing counterparty group${(this.groupedOutgoingRows.length || this.outgoingRows.length) === 1 ? '' : 's'}.`,
          show: true
        },
        {
          label: 'Incoming exposure',
          value: this.formatUsd(this.incomingTotal),
          hint: `${this.groupedIncomingRows.length || this.incomingRows.length} incoming counterparty group${(this.groupedIncomingRows.length || this.incomingRows.length) === 1 ? '' : 's'}.`,
          show: true
        }
      ];
    },
    visibleStatCards() {
      return this.statCards.filter((stat) => stat.show !== false);
    },
    transferStatusLabel() {
      if (this.transferStatus === 'running') return 'Loading transfers';
      if (this.transferStatus === 'warning') return 'Transfer warning';
      if (this.transferRows.length) return `${this.transferRows.length} transfers loaded`;
      return 'No transfers loaded';
    },
    transferStatusClass() {
      if (this.transferStatus === 'running') return 'status-pill--unverified';
      if (this.transferStatus === 'warning') return 'status-pill--needs_review';
      if (this.transferRows.length) return 'status-pill--ready';
      return 'status-pill--placeholder';
    },
    transferDrilldownTitle() {
      if (!this.selectedCounterparty) return 'Select a counterparty';
      const direction = this.selectedFlow === 'in' ? 'incoming from' : 'outgoing to';
      return `${direction} ${this.counterpartyPrimaryName(this.selectedCounterparty) || 'counterparty'}`;
    },
    selectedTransfers() {
      return this.transferRows.filter((row) => this.selectedTransferKeys.includes(this.transferRowKey(row)));
    },
    targetPayload() {
      return {
        kind: this.form.targetKind,
        value: this.form.targetKind === 'entity' ? this.selectedEntityId : this.form.address.trim(),
        address: this.form.targetKind === 'address' ? this.form.address.trim() : '',
        entityId: this.form.targetKind === 'entity' ? this.selectedEntityId : (this.result?.identity?.entityId || ''),
        displayName: this.previewTitle
      };
    }
  },
  methods: {
    setActiveSegment(id) {
      this.activeSegmentId = id || '';
    },
    clearActiveSegment() {
      this.activeSegmentId = '';
    },
    isSegmentActive(row) {
      return Boolean(this.activeSegmentId && row?.id === this.activeSegmentId);
    },
    scrollToElementWithOffset(target, offset = 88) {
      const element = Array.isArray(target) ? target[0] : target;
      if (!element) return;

      if (typeof window === 'undefined' || typeof element.getBoundingClientRect !== 'function') {
        element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }

      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
    },
    scrollToVisualWorkspace() {
      this.lookupCueVisible = false;
      this.$nextTick(() => {
        this.scrollToElementWithOffset(
          this.$refs.wheelJumpTarget || this.$refs.donutCard || this.$refs.visualWorkspace,
          88
        );
      });
    },
    scrollToCounterpartyDetail() {
      this.lookupCueVisible = false;
      this.$nextTick(() => {
        this.scrollToElementWithOffset(this.$refs.counterpartyDetail, 88);
      });
    },
    setTargetKind(kind) {
      this.form.targetKind = kind;
      this.error = '';
      this.result = null;
      this.transferRows = [];
      this.selectedTransferKeys = [];
      this.selectedCounterparty = null;
      this.lookupCueVisible = false;
      this.activeSegmentId = '';
    },
    async searchEntities() {
      const query = this.form.entityQuery.trim();
      if (!query) return;
      this.entitySearchStatus = 'running';
      this.error = '';

      try {
        const response = await searchExposureTargets({ query, limit: 8 });
        this.entitySearchResults = Array.isArray(response.results) ? response.results : [];
        if (!this.entitySearchResults.length) {
          this.$emit('flash', 'No Arkham entity matches were returned for that search.');
        }
      } catch (error) {
        this.error = error?.message || 'Could not search Arkham entities.';
        this.$emit('error', this.error);
      } finally {
        this.entitySearchStatus = 'idle';
      }
    },
    selectEntity(result) {
      this.form.selectedEntity = result;
      this.form.entityQuery = result.displayName || result.entityId || result.id || this.form.entityQuery;
    },
    isSelectedEntity(result) {
      const selectedId = this.selectedEntityId;
      return Boolean(selectedId && selectedId === (result.entityId || result.id));
    },
    async runLookup({ forceRefresh = false } = {}) {
      if (!this.canRunLookup) {
        this.error = this.form.targetKind === 'entity' ? 'Search for and select an entity first.' : 'Enter an address first.';
        return;
      }

      this.lookupStatus = 'running';
      this.error = '';
      this.transferRows = [];
      this.selectedTransferKeys = [];
      this.selectedCounterparty = null;
      this.lookupCueVisible = false;
      this.activeSegmentId = '';

      try {
        const response = await lookupExposureReview({
          targetKind: this.form.targetKind,
          address: this.form.targetKind === 'address' ? this.form.address.trim() : '',
          entityId: this.form.targetKind === 'entity' ? this.selectedEntityId : '',
          filters: this.normalizedFilters,
          forceRefresh
        });
        this.result = response;
        this.lookupStatus = response.status === 'warning' ? 'warning' : 'complete';
        this.lookupCueVisible = true;
      } catch (error) {
        this.lookupStatus = 'error';
        this.error = error?.message || 'Could not run exposure lookup.';
        this.$emit('error', this.error);
      }
    },
    async viewTransfersForDisplayRow(row, preferredFlow = '') {
      if (!row || row.isOther) return;
      const flow = preferredFlow || (row.direction === 'mixed' ? this.firstAvailableFlow(row) : row.direction) || this.wheelMode || 'out';
      const sourceRow = this.representativeRowForFlow(row, flow);

      if (!sourceRow) {
        this.$emit('flash', 'No representative counterparty row is available for that drilldown.');
        return;
      }

      return this.viewTransfers(sourceRow, flow);
    },
    async viewTransfers(row, flow, { forceRefresh = false } = {}) {
      if (!this.result) return;
      this.selectedCounterparty = row;
      this.selectedFlow = flow === 'in' ? 'in' : 'out';
      this.transferStatus = 'running';
      this.transferRows = [];
      this.selectedTransferKeys = [];
      this.error = '';

      try {
        const response = await lookupExposureTransfers({
          target: this.targetPayload,
          counterparty: row,
          flow: this.selectedFlow,
          filters: this.normalizedFilters,
          limit: Math.min(50, Number(this.form.filters.limit) || 25),
          forceRefresh
        });
        this.transferRows = Array.isArray(response.transfers) ? response.transfers : [];
        this.transferStatus = response.status === 'warning' ? 'warning' : 'complete';
      } catch (error) {
        this.transferStatus = 'warning';
        this.error = error?.message || 'Could not load transfer drilldown.';
        this.$emit('error', this.error);
      }
    },
    refreshTransfers() {
      if (!this.selectedCounterparty) return;
      return this.viewTransfers(this.selectedCounterparty, this.selectedFlow, { forceRefresh: true });
    },
    groupCounterpartyRows(rows, mode = 'directional') {
      const groups = new Map();

      (rows || []).forEach((row) => {
        const direction = row.direction === 'in' ? 'in' : 'out';
        const identityKey = this.counterpartyIdentityKey(row);
        const key = `${mode === 'combined' ? 'combined' : direction}:${identityKey}`;
        const usd = Number(row.usd) || 0;
        const transactionCount = Number(row.transactionCount || row.txCount || row.count) || 0;

        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            groupKey: identityKey,
            direction: mode === 'combined' ? direction : direction,
            directions: [direction],
            displayName: this.counterpartyPrimaryName(row),
            entityId: row.entityId || '',
            address: row.address || '',
            type: row.type || '',
            tags: this.counterpartyChips(row),
            usd: 0,
            transactionCount: 0,
            rowCount: 0,
            rawRows: [],
            rawRowsByDirection: { in: [], out: [] },
            rawRow: row
          });
        }

        const group = groups.get(key);
        group.usd += usd;
        group.transactionCount += transactionCount;
        group.rowCount += 1;
        group.rawRows.push(row);
        group.rawRowsByDirection[direction].push(row);
        group.directions = Array.from(new Set([...group.directions, direction]));
        group.direction = mode === 'combined' && group.directions.length > 1 ? 'mixed' : group.directions[0];
        group.tags = Array.from(new Set([...(group.tags || []), ...this.counterpartyChips(row)])).filter(Boolean);

        if (usd > (Number(group.rawRow?.usd) || 0)) {
          group.rawRow = row;
          group.displayName = this.counterpartyPrimaryName(row);
          group.entityId = row.entityId || group.entityId;
          group.address = row.address || group.address;
          group.type = row.type || group.type;
        }
      });

      return Array.from(groups.values()).sort((a, b) => (Number(b.usd) || 0) - (Number(a.usd) || 0));
    },
    representativeRowForFlow(row, flow) {
      if (!row) return null;
      if (Array.isArray(row.rawRows)) {
        const direction = flow === 'in' ? 'in' : 'out';
        const matchingRows = row.rawRowsByDirection?.[direction] || row.rawRows.filter((entry) => entry.direction === direction);
        return matchingRows.slice().sort((a, b) => (Number(b.usd) || 0) - (Number(a.usd) || 0))[0] || row.rawRow || null;
      }
      return row;
    },
    firstAvailableFlow(row) {
      if (this.hasFlow(row, 'out')) return 'out';
      if (this.hasFlow(row, 'in')) return 'in';
      return row?.direction === 'in' ? 'in' : 'out';
    },
    hasFlow(row, flow) {
      if (!row) return false;
      if (Array.isArray(row.rawRows)) {
        return Boolean(row.rawRowsByDirection?.[flow]?.length || row.rawRows.some((entry) => entry.direction === flow));
      }
      return row.direction === flow;
    },
    counterpartyIdentityKey(row = {}) {
      const candidates = [
        row.entityId ? `entity:${row.entityId}` : '',
        row.entityName ? `name:${row.entityName}` : '',
        row.displayName && !this.looksLikeAddress(row.displayName) ? `display:${row.displayName}` : '',
        row.labelName ? `label:${row.labelName}` : '',
        row.address ? `address:${row.address}` : '',
        row.id ? `id:${row.id}` : ''
      ];

      return String(candidates.find(Boolean) || 'unknown').toLowerCase();
    },
    counterpartyPrimaryName(row = {}) {
      const rawName = row.entityName || row.displayName || row.labelName || row.label || row.name || '';
      const address = row.address || (this.looksLikeAddress(row.id) ? row.id : '');

      if (rawName && !this.looksLikeAddress(rawName)) return rawName;
      if (address) return this.compactValue(address);
      if (rawName) return this.compactValue(rawName);
      if (row.entityId) return row.entityId;
      if (row.id) return this.compactValue(row.id);
      return 'Unknown counterparty';
    },
    counterpartySecondaryLine(row = {}) {
      const primary = this.counterpartyPrimaryName(row).toLowerCase();
      const parts = [];
      const entityId = String(row.entityId || '').trim();
      const address = String(row.address || '').trim();
      const rowCount = Number(row.rowCount || 0);

      if (entityId && entityId.toLowerCase() !== primary) parts.push(`entity: ${entityId}`);
      if (address && this.compactValue(address).toLowerCase() !== primary && address.toLowerCase() !== entityId.toLowerCase()) {
        parts.push(this.compactValue(address));
      }
      if (rowCount > 1) parts.push(`${rowCount} grouped rows`);

      return parts.join(' · ');
    },
    counterpartyChips(row = {}) {
      const chips = [];
      if (row.type) chips.push(row.type);
      if (Array.isArray(row.tags)) chips.push(...row.tags);
      if (!chips.length && row.entityId) chips.push('entity');
      if (!chips.length && row.address) chips.push('address');
      return Array.from(new Set(chips.map((chip) => String(chip || '').trim()).filter(Boolean)));
    },
    counterpartyTableKey(row = {}) {
      return row.id || `${row.direction || 'row'}:${this.counterpartyIdentityKey(row)}:${row.usd || 0}:${row.transactionCount || 0}`;
    },
    rowDirectionLabel(row = {}) {
      if (row.direction === 'mixed') return 'Incoming + outgoing';
      return row.direction === 'in' ? 'Incoming' : 'Outgoing';
    },
    looksLikeAddress(value) {
      const text = String(value || '').trim();
      return /^0x[a-fA-F0-9]{20,}$/.test(text) || /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{20,}$/i.test(text) || /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(text);
    },
    transferRowKey(row) {
      return row.txHash || row.id || JSON.stringify([row.time, row.fromAddress, row.toAddress, row.usd]);
    },
    isTransferSelected(row) {
      return this.selectedTransferKeys.includes(this.transferRowKey(row));
    },
    toggleTransferSelection(row) {
      const key = this.transferRowKey(row);
      if (this.selectedTransferKeys.includes(key)) {
        this.selectedTransferKeys = this.selectedTransferKeys.filter((entry) => entry !== key);
      } else {
        this.selectedTransferKeys = [...this.selectedTransferKeys, key];
      }
    },
    emitSaveSource() {
      if (!this.targetPayload.value) {
        this.error = this.form.targetKind === 'entity' ? 'Select an entity before saving.' : 'Enter an address before saving.';
        return;
      }

      this.$emit('save-source', this.buildSourcePayload('captured'));
    },
    emitStageTransfers() {
      if (!this.selectedTransfers.length) return;
      this.$emit('stage-transfers', {
        ...this.buildSourcePayload('pending_validation'),
        selectedTransfers: this.selectedTransfers,
        selectedCounterparty: this.selectedCounterparty,
        selectedFlow: this.selectedFlow
      });
    },
    buildSourcePayload(status) {
      return {
        status,
        target: this.targetPayload,
        label: this.form.label.trim(),
        note: this.form.note.trim(),
        filters: this.normalizedFilters,
        lookupResult: this.result,
        savedAt: new Date().toISOString()
      };
    },
    formatUsd(value) {
      const number = Number(value || 0);
      if (!Number.isFinite(number) || number <= 0) return '$0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: number >= 1000000 ? 'compact' : 'standard',
        maximumFractionDigits: number >= 1000000 ? 1 : 0
      }).format(number);
    },
    formatCount(value) {
      const number = Number(value || 0);
      if (!Number.isFinite(number) || number <= 0) return '—';
      return new Intl.NumberFormat('en-US').format(number);
    },
    formatPercent(value, total) {
      const denominator = Number(total) || 0;
      const number = Number(value) || 0;
      if (!denominator || !number) return '0%';
      return `${Math.round((number / denominator) * 100)}%`;
    },
    formatDate(value) {
      if (!value) return '—';
      try {
        return new Intl.DateTimeFormat('en-CA', {
          dateStyle: 'medium',
          timeStyle: 'short'
        }).format(new Date(value));
      } catch (error) {
        return value;
      }
    },
    compactValue(value) {
      const text = String(value || '').trim();
      if (!text) return '';
      if (text.length <= 24) return text;
      return `${text.slice(0, 10)}…${text.slice(-8)}`;
    }
  }
};
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.panel {
  padding: 1.35rem;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 1rem;
}

.exposure-review-step--workspace {
  gap: 1.15rem;
  background:
    radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.10), transparent 28%),
    radial-gradient(circle at 92% 10%, rgba(14, 165, 233, 0.12), transparent 26%),
    #ffffff;
}

.exposure-hero,
.visual-workspace-header,
.analysis-stats-card__header,
.donut-card__header,
.exposure-dossier-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.exposure-hero p,
.section-heading p,
.visual-workspace-header p,
.donut-card__header p,
.analysis-stats-card__header p,
.address-analysis-summary__description,
.analysis-mini-card p,
.empty-state-copy {
  margin: .45rem 0 0;
  color: #475569;
  line-height: 1.6;
}

.exposure-hero h2,
.section-heading h3,
.visual-workspace-header h3,
.donut-card h3,
.analysis-stats-card h3,
.exposure-dossier-card h3 {
  margin: 0;
  color: #0f172a;
}

.exposure-hero__badges,
.chip-list,
.table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: .45rem;
}

.eyebrow,
.address-analysis-summary__eyebrow,
.analysis-section-eyebrow {
  margin: 0 0 .35rem;
  color: #2563eb;
  font-size: .8rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.exposure-workspace-grid {
  display: grid;
  grid-template-columns: minmax(300px, .78fr) minmax(0, 1.22fr);
  gap: 1rem;
  align-items: stretch;
  min-width: 0;
}

.exposure-target-panel,
.exposure-dossier-card,
.exposure-visual-workspace,
.analysis-stats-card,
.donut-card,
.top-group-card {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: rgba(248, 250, 252, 0.92);
  min-width: 0;
  overflow: hidden;
}

.exposure-target-panel,
.exposure-dossier-card,
.exposure-visual-workspace,
.analysis-stats-card,
.top-group-card {
  display: grid;
  gap: 1rem;
}

.exposure-dossier-card {
  align-content: start;
}

.exposure-target-panel > *,
.exposure-dossier-card > *,
.exposure-visual-workspace > *,
.donut-card > *,
.top-group-card > * {
  min-width: 0;
}

.target-kind-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .65rem;
}

.target-kind-card {
  display: grid;
  gap: .25rem;
  padding: .8rem;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  text-align: left;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  min-width: 0;
}

.target-kind-card span {
  color: #2563eb;
  font-size: .78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.target-kind-card strong {
  font-size: .95rem;
}

.target-kind-card--active {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}

.field {
  display: grid;
  gap: .4rem;
  min-width: 0;
}

.field span {
  font-size: .85rem;
  font-weight: 800;
  color: #334155;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: .7rem .8rem;
  font: inherit;
  color: #0f172a;
  background: #ffffff;
  min-width: 0;
  max-width: 100%;
}

textarea {
  min-height: 96px;
  resize: vertical;
}

button {
  font: inherit;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .35rem;
  min-height: 40px;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: .6rem 1rem;
  font-weight: 800;
  cursor: pointer;
  white-space: normal;
}

.btn:disabled,
.donut-legend-row:disabled {
  cursor: not-allowed;
  opacity: .58;
}

.btn--primary {
  color: #ffffff;
  background: #2563eb;
}

.btn--secondary {
  color: #1d4ed8;
  background: #dbeafe;
  border-color: #bfdbfe;
}

.btn--ghost {
  color: #334155;
  background: #ffffff;
  border-color: #cbd5e1;
}

.btn--small {
  min-height: 32px;
  padding: .35rem .65rem;
  font-size: .85rem;
}

.panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
  min-width: 0;
}

.panel__actions .btn {
  max-width: 100%;
}

.inline-search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(88px, auto);
  gap: .6rem;
}

.entity-result-list {
  display: grid;
  gap: .6rem;
  max-height: 360px;
  overflow: auto;
  padding-right: .2rem;
}

.entity-result-card,
.selected-entity-card {
  display: grid;
  gap: .2rem;
  width: 100%;
  text-align: left;
  padding: .75rem;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background: #ffffff;
  color: #0f172a;
}

.entity-result-card {
  cursor: pointer;
}

.entity-result-card--selected {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}

.entity-result-card span,
.selected-entity-card span,
.entity-result-card small {
  color: #64748b;
}

.selected-entity-card code,
.counterparty-cell code,
.tx-table code,
.exposure-dossier-card code {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
  color: #334155;
}

.advanced-settings {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
  min-width: 0;
}

.advanced-settings summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .85rem;
  padding: .8rem .9rem;
  cursor: pointer;
  list-style: none;
}

.advanced-settings summary::-webkit-details-marker {
  display: none;
}

.advanced-settings summary span {
  font-weight: 900;
  color: #0f172a;
}

.advanced-settings summary small {
  color: #64748b;
  text-align: right;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.advanced-settings[open] summary {
  border-bottom: 1px solid #e2e8f0;
}

.exposure-filter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .85rem;
  padding: .9rem;
}

.advanced-settings--notes .field {
  padding: .9rem;
}

.dossier-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .8rem;
}

.analysis-mini-card {
  padding: .85rem;
  border-radius: 16px;
  border: 1px solid #dbeafe;
  background: #ffffff;
}

.analysis-mini-card__label {
  display: block;
  color: #64748b;
  font-size: .78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-bottom: .3rem;
}

.analysis-chip,
.status-pill,
.analysis-wheel-card__state,
.type-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: .35rem .6rem;
  font-size: .78rem;
  font-weight: 800;
}

.analysis-chip {
  background: #e0f2fe;
  color: #0369a1;
}

.status-pill,
.analysis-wheel-card__state {
  background: #e2e8f0;
  color: #334155;
  white-space: nowrap;
}

.status-pill--ready {
  background: #dcfce7;
  color: #166534;
}

.status-pill--unverified {
  background: #fef3c7;
  color: #92400e;
}

.status-pill--needs_review {
  background: #fee2e2;
  color: #991b1b;
}

.status-pill--placeholder {
  background: #e2e8f0;
  color: #334155;
}

.post-lookup-cue {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .85rem;
  padding: .75rem .9rem;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0fdf4, #eff6ff);
  color: #14532d;
}

.post-lookup-cue div {
  display: grid;
  gap: .12rem;
  min-width: 0;
}

.post-lookup-cue span {
  color: #166534;
  line-height: 1.45;
}

.snapshot-runway {
  display: grid;
  gap: .9rem;
  padding: 1rem;
  border: 1px solid #dbeafe;
  border-radius: 18px;
  background:
    radial-gradient(circle at 94% 8%, rgba(37, 99, 235, 0.16), transparent 28%),
    linear-gradient(135deg, rgba(239, 246, 255, 0.98), rgba(248, 250, 252, 0.98));
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.snapshot-runway--running {
  border-color: #93c5fd;
}

.snapshot-runway--complete {
  border-color: #bfdbfe;
}

.snapshot-runway--warning {
  border-color: #fde68a;
  background:
    radial-gradient(circle at 94% 8%, rgba(245, 158, 11, 0.15), transparent 28%),
    linear-gradient(135deg, rgba(255, 251, 235, 0.98), rgba(248, 250, 252, 0.98));
}

.snapshot-runway__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: .75rem;
}

.snapshot-runway__header > div {
  display: grid;
  gap: .25rem;
  min-width: 0;
}

.snapshot-runway__header strong {
  color: #0f172a;
  font-size: 1rem;
}

.snapshot-runway__header span:not(.snapshot-runway__pulse) {
  color: #475569;
  font-size: .92rem;
  line-height: 1.45;
}

.snapshot-runway__pulse {
  width: .85rem;
  height: .85rem;
  margin-top: .25rem;
  border-radius: 999px;
  background: #2563eb;
  box-shadow: 0 0 0 .35rem rgba(37, 99, 235, 0.13);
  flex: 0 0 auto;
}

.snapshot-runway--idle .snapshot-runway__pulse,
.snapshot-runway--warning .snapshot-runway__pulse {
  background: #f59e0b;
  box-shadow: 0 0 0 .35rem rgba(245, 158, 11, 0.13);
}

.snapshot-runway--running .snapshot-runway__pulse {
  animation: snapshotPulse 1.4s ease-in-out infinite;
}

.snapshot-runway__steps {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .65rem;
}

.snapshot-step {
  display: flex;
  align-items: center;
  gap: .6rem;
  min-width: 0;
  padding: .68rem .75rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.84);
}

.snapshot-step__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: .85rem;
  font-weight: 900;
  flex: 0 0 auto;
}

.snapshot-step__copy {
  display: grid;
  gap: .1rem;
  min-width: 0;
}

.snapshot-step__copy strong {
  color: #0f172a;
  font-size: .9rem;
  line-height: 1.2;
}

.snapshot-step__copy small {
  color: #64748b;
  font-size: .78rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.snapshot-step--complete {
  border-color: #bfdbfe;
  background: rgba(239, 246, 255, 0.92);
}

.snapshot-step--complete .snapshot-step__icon {
  background: #2563eb;
  color: #ffffff;
}

.snapshot-step--active {
  border-color: #93c5fd;
  background: rgba(219, 234, 254, 0.94);
}

.snapshot-step--active .snapshot-step__icon {
  background: #60a5fa;
  color: #ffffff;
  animation: snapshotPulse 1.4s ease-in-out infinite;
}

.snapshot-step--warning {
  border-color: #fde68a;
  background: rgba(255, 251, 235, 0.94);
}

.snapshot-step--warning .snapshot-step__icon {
  background: #f59e0b;
  color: #ffffff;
}

.snapshot-runway__callout {
  display: grid;
  gap: .7rem;
}

.snapshot-runway__callout p {
  margin: 0;
  color: #334155;
  line-height: 1.55;
}

.snapshot-runway__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .55rem;
}

@keyframes snapshotPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, .22); }
  50% { box-shadow: 0 0 0 5px rgba(37, 99, 235, .10); }
}

.visual-workspace-header {
  align-items: center;
}

.review-toggle-stack {
  display: grid;
  justify-items: end;
  gap: .5rem;
}

.segmented-control {
  display: inline-flex;
  flex-wrap: wrap;
  gap: .35rem;
  padding: .25rem;
  border-radius: 999px;
  background: #e2e8f0;
}

.segmented-control__button {
  border: 0;
  border-radius: 999px;
  padding: .55rem .85rem;
  color: #334155;
  background: transparent;
  font-weight: 800;
  cursor: pointer;
}

.segmented-control__button--active {
  color: #ffffff;
  background: #2563eb;
}

.visual-workspace-grid {
  display: grid;
  grid-template-columns: minmax(360px, .95fr) minmax(300px, 1.05fr);
  gap: 1rem;
  align-items: stretch;
  min-width: 0;
}

.donut-card {
  display: grid;
  gap: 1rem;
  background: #ffffff;
}

.donut-layout {
  display: grid;
  grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
  gap: .75rem;
  align-items: center;
  overflow: visible;
  scroll-margin-top: 88px;
}

.donut-chart {
  position: relative;
  width: min(100%, 300px);
  aspect-ratio: 1;
  border-radius: 999px;
  display: grid;
  place-items: center;
  margin: 0 auto;
  overflow: visible;
  filter: drop-shadow(0 18px 30px rgba(15, 23, 42, .12));
}

.donut-chart__svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.donut-chart__track,
.donut-chart__segment {
  fill: none;
  stroke-width: 14;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

.donut-chart__track {
  stroke: #e2e8f0;
}

.donut-chart__segment {
  cursor: pointer;
  transition: opacity .18s ease, stroke-width .18s ease, filter .18s ease;
}

.donut-chart__segment--active {
  stroke-width: 17;
  filter: drop-shadow(0 0 5px rgba(15, 23, 42, .24));
}

.donut-chart__segment--muted {
  opacity: .28;
}

.donut-chart__center {
  position: absolute;
  inset: 29%;
  border-radius: 999px;
  display: grid;
  place-items: center;
  text-align: center;
  background: #ffffff;
  padding: .75rem;
  box-shadow: 0 0 0 1px rgba(226, 232, 240, .9);
  pointer-events: none;
}

.donut-chart__center span {
  color: #64748b;
  font-size: .78rem;
  font-weight: 800;
}

.donut-chart__center strong {
  color: #0f172a;
  font-size: 1.15rem;
}

.donut-chart__center small {
  color: #2563eb;
  font-size: .76rem;
  font-weight: 900;
}

.donut-legend,
.group-list {
  display: grid;
  gap: .35rem;
  user-select: text;
}

.donut-legend {
  position: relative;
  padding-left: .35rem;
}

.donut-legend::before {
  content: '';
  position: absolute;
  left: -.45rem;
  top: .45rem;
  bottom: .45rem;
  border-left: 1px dashed rgba(37, 99, 235, .22);
}

.donut-legend-row,
.group-list-row {
  position: relative;
  display: grid;
  gap: .45rem;
  align-items: center;
  width: 100%;
  padding: .5rem .55rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  text-align: left;
  color: #0f172a;
  transition: border-color .16s ease, background .16s ease, box-shadow .16s ease, opacity .16s ease;
  user-select: text;
}

.donut-legend-row {
  grid-template-columns: auto minmax(0, 1fr) auto;
}

.donut-legend-row::before {
  content: '';
  position: absolute;
  left: -1.05rem;
  top: 50%;
  width: 1rem;
  border-top: 1px dashed rgba(37, 99, 235, .20);
}

.group-list-row {
  grid-template-columns: minmax(0, 1fr) auto auto;
}

.donut-legend-row--active,
.group-list-row--active {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .10);
}

.donut-legend-row--active::before {
  border-top-color: rgba(37, 99, 235, .78);
}

.donut-legend-row--muted,
.group-list-row--muted {
  opacity: .48;
}

.donut-legend-row__swatch {
  width: .72rem;
  height: .72rem;
  border-radius: 999px;
}

.donut-legend-row__main,
.group-list-row__main,
.group-list-row__meta,
.counterparty-cell {
  display: grid;
  gap: .18rem;
  min-width: 0;
}

.group-list-row__meta {
  justify-items: end;
}

.donut-legend-row small,
.group-list-row small,
.counterparty-cell code {
  color: #64748b;
  line-height: 1.25;
}

.donut-legend-row strong,
.group-list-row strong {
  line-height: 1.2;
}

.donut-legend-row__action {
  min-height: 28px;
  padding: .25rem .55rem;
}

.type-chip {
  background: #eef2ff;
  color: #3730a3;
}

.validation-warning,
.validation-summary {
  padding: .85rem;
  border-radius: 14px;
  line-height: 1.5;
}

.validation-warning--strong {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.validation-summary {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
}

.table-scroll {
  overflow-x: auto;
}

.tx-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.tx-table th,
.tx-table td {
  padding: .75rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}

.tx-table th {
  color: #475569;
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.transfer-drilldown-card {
  border-color: #bfdbfe;
}

@media (max-width: 1180px) {
  .exposure-workspace-grid,
  .visual-workspace-grid,
  .donut-layout {
    grid-template-columns: 1fr;
  }

  .review-toggle-stack {
    justify-items: start;
  }
}

@media (max-width: 760px) {
  .exposure-hero,
  .visual-workspace-header,
  .analysis-stats-card__header,
  .donut-card__header,
  .exposure-dossier-card__top {
    display: grid;
  }

  .target-kind-grid,
  .dossier-kpi-grid,
  .exposure-filter-grid {
    grid-template-columns: 1fr;
  }

  .inline-search {
    grid-template-columns: 1fr;
  }

  .review-toggle-stack {
    width: 100%;
  }

  .post-lookup-cue,
  .group-list-row,
  .donut-legend-row {
    display: grid;
    grid-template-columns: 1fr;
  }

  .snapshot-runway__steps {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .snapshot-runway__steps {
    grid-template-columns: 1fr;
  }
}

</style>
